'use client';

import { cn } from '../utils';

interface SwitchProps {
  'aria-label'?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'default';
  /** Class name for the outer track. */
  className?: string;
  /** Class name for the inner thumb — useful for overriding the thumb
   *  colour per consumer (e.g. brand colours on specific feature panels). */
  thumbClassName?: string;
}

function Switch({
  'aria-label': ariaLabel,
  checked,
  onCheckedChange,
  disabled = false,
  size = 'default',
  className,
  thumbClassName,
}: SwitchProps) {
  const isSmall = size === 'sm';

  return (
    <button
      type="button"
      role="switch"
      aria-label={ariaLabel}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out',
        'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // OFF is the state that has to prove the control EXISTS, and in light
        // `line` is #EEEEEE — 1.16:1 against the card it sits on, with a white
        // thumb 1.16:1 against the track, so the whole switch was a ghost.
        // `disabled:opacity-50` then halves both. `line-strong` is 1.66:1 for
        // the track and gives the thumb the same edge to read against.
        //
        // Scoped to LIGHT: in dark `line` is #333333 (1.53:1) and already
        // reads, and this atom is end-user reachable — `theme-light` never
        // matches `visitor`, so the public surfaces are untouched.
        checked ? 'bg-action' : 'bg-line-strong',
        isSmall ? 'h-6 w-11' : 'h-7 w-12',
        className,
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block rounded-full shadow-sm transition-all duration-200 ease-in-out',
          checked ? 'bg-surface-1' : 'theme-keep bg-white',
          isSmall ? 'h-5 w-5' : 'h-6 w-6',
          isSmall ? 'translate-y-0.5' : 'translate-y-0.5',
          checked
            ? isSmall
              ? 'translate-x-[22px]'
              : 'translate-x-[22px]'
            : 'translate-x-0.5',
          thumbClassName,
        )}
      />
    </button>
  );
}

export { Switch };
export type { SwitchProps };
